import { Ga4Item } from "@/utils/analytics";
import { Api } from "@/api";
import {
  CheckoutMetadata,
  CheckoutSession,
  CouponWithPromoCodes,
  StripeShippingOption,
} from "@/utils/types/types";
import type { Stripe } from "stripe";

// Temporary admin-only verification aid: per-line stored (visualizer) price
// vs the /quote price, in cents. Present only when an admin requests checkout.
export type PriceComparisonLine = {
  lineItemId: string;
  name: string;
  quantity: number;
  storedCents: number;
  quotedCents: number | null;
  deltaCents: number | null;
  quoteOk: boolean;
  chargedFrom: "quote" | "stored";
  // Admin debugging: the exact payload sent to /quote and the returned breakdown.
  quoteItem?: Record<string, unknown> | null;
  breakdown?: unknown;
};

export type CheckoutApiResponse = {
  session: CheckoutSession;
  /** GA4 purchase-event items built server-side from the cart's stored line
   *  items (WebFX tracking, 2026-08-31). Optional/best-effort. */
  ga4Items?: Ga4Item[];
  shippingPickupOption: StripeShippingOption;
  shippingOversizedOption: StripeShippingOption | null;
  shippingOptions: StripeShippingOption[];
  priceComparison?: PriceComparisonLine[];
};

export type SavedPaymentMethod = {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
};

export const StripeApi = {
  // ── Saved cards (staging 2026-08-25) ──
  async getPaymentMethods() {
    return Api.get<{ paymentMethods: SavedPaymentMethod[] }>(
      `/api/stripe/payment-methods`
    );
  },
  async createCardSetupIntent() {
    return Api.post<{ clientSecret: string }>(
      `/api/stripe/payment-methods/setup-intent`,
      {}
    );
  },
  async detachPaymentMethod(paymentMethodId: string) {
    return Api.post<{ ok: boolean }>(`/api/stripe/payment-methods/detach`, {
      paymentMethodId,
    });
  },
  async setDefaultPaymentMethod(paymentMethodId: string) {
    return Api.post<{ ok: boolean }>(`/api/stripe/payment-methods/default`, {
      paymentMethodId,
    });
  },
  async checkout(payload: { cartId: string; parentOrderNo?: string }) {
    return Api.post<CheckoutApiResponse>(`/api/stripe/checkout`, {
      cartId: payload.cartId,
      parentOrderNo: payload.parentOrderNo || "",
    });
  },
  async getCheckout(id: string) {
    return Api.get<CheckoutApiResponse>(`/api/stripe/checkout/${id}`);
  },
  async finalizeCheckout(payload: {
    cartId: string;
    currentSessionId: string;
    parentOrderNo?: string;
    whiteLabel?: boolean;
    gift?: boolean;
    note?: string;
    shippingType?: string;
    shippingInfo?: {
      firstName: string;
      lastName: string;
      companyName?: string;
      street: string;
      apartment?: string;
      city: string;
      state: string;
      zipcode: string;
    };
    deliveryAddress?: {
      firstName: string;
      lastName: string;
      companyName?: string;
      street: string;
      apartment?: string;
      city: string;
      state: string;
      zipcode: string;
    };
    selectedShippingRateId?: string;
    promotionCode?: string;
  }) {
    return Api.post<CheckoutApiResponse>(
      `/api/stripe/checkout/finalize`,
      payload
    );
  },
  async updateCheckout(
    id: string,
    payload: {
      metadata?: CheckoutMetadata;
      shippingRateIds?: string[];
    }
  ) {
    return Api.put(`/api/stripe/checkout/${id}`, payload);
  },

  async getCoupons() {
    return Api.get<CouponWithPromoCodes[]>(`/api/stripe/coupons`);
  },
  async createCoupon(payload: Stripe.CouponCreateParams) {
    return Api.post<Stripe.Coupon>(`/api/stripe/coupons`, payload);
  },
  async deleteCoupon(id: string) {
    return Api.delete(`/api/stripe/coupons/${id}`);
  },

  async createPromocode(payload: Stripe.PromotionCodeCreateParams) {
    return Api.post<Stripe.PromotionCode>(`/api/stripe/promocodes`, payload);
  },

  async updatePromocode(id: string, payload: Stripe.PromotionCodeUpdateParams) {
    return Api.put<Stripe.PromotionCode>(
      `/api/stripe/promocodes/${id}`,
      payload
    );
  },

  async getCartShippingStatus(payload: { cartId: string }) {
    return Api.get<{ shippingStatus: string }>(
      `/api/stripe/cart-shipping-status?cartId=${encodeURIComponent(payload.cartId)}`
    );
  },

  async getDeliveryMethods(payload: {
    cartId: string;
    shippingInfo: {
      firstName: string;
      lastName: string;
      street: string;
      city: string;
      state: string;
      zipcode: string;
    };
    isPickup?: boolean;
  }) {
    return Api.post<{
      deliveryMethods: {
        id: string;
        key: string;
        displayName: string;
        price: number;
        priceInCents: number;
        currency: string;
        metadata: Record<string, unknown>;
      }[];
      pickupOption: {
        id: string;
        key: string;
        displayName: string;
        price: number;
        priceInCents: number;
        currency: string;
        metadata: Record<string, unknown>;
      };
      oversizedOption: {
        id: string;
        key: string;
        displayName: string;
        price: number;
        priceInCents: number;
        currency: string;
        metadata: Record<string, unknown>;
      } | null;
      cartInfo: {
        subtotal: number;
        subtotalInCents: number;
        isOver100USD: boolean;
        dynamicShippingCalculated: boolean;
        calculatedShippingPrice: number;
        shippingStatus: string;
      };
    }>(`/api/stripe/delivery-methods`, payload);
  },
};
